import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import Joi from 'joi';

import { useUpdateUserMutation } from 'api/main';
import { useSendCoSubscriberMutation } from 'api/tunnelSteps';
import { tunnelSelectors, userSelectors } from 'reducers';
import { ineligibleSet, validationHandlerSet } from 'reducers/tunnel';
import { dateValidator, validateSchema } from 'utils/helpers';
import { tunnelSchemas } from 'schemas';
import {
    EMAIL_VALIDATION_OPTIONS,
    InternalErrors,
    investorErrors,
    NAME_REGEX,
} from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { TunnelBox } from 'containers/tunnel/common';
import {
    MemoizedFiscalSituationFormFCPRPP,
    MemoizedGeneralInfoForm,
    MemoizedPoliticallyExposedForm,
    MemoizedSituationFormFPCIPP,
} from '../contact-info/memoized-components/MemoizedComponents';
import { FullAddressForm, SubmitButton, SuccessMessage } from 'components';

const schema = {
    firstName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
        'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_PRENOM,
        'string.min': texts.FIRST_NAME_LENGTH,
        'string.max': texts.FIRST_NAME_LENGTH,
        'string.pattern.base': texts.CO_SUBSCRIBER_FIRST_NAME_PATTERN,
    }),
    lastName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
        'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_NOM,
        'string.min': texts.LAST_NAME_LENGTH,
        'string.max': texts.LAST_NAME_LENGTH,
        'string.pattern.base': texts.CO_SUBSCRIBER_LAST_NAME_PATTERN,
    }),
    email: Joi.string().required().email(EMAIL_VALIDATION_OPTIONS).messages({
        'string.empty': texts.SAISIR_VOTRE_ADRESSE_EMAIL,
        'string.email': texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE,
    }),
    noOfPersons: Joi.number().min(0).max(19).required().messages({
        'string.empty': texts.VEUILLEZ_SAISIR_LE_NOMBRE_DE_PERSONNES,
    }),
    hasDifferentFiscalAddress: Joi.boolean().valid(true, false),
    fiscalAddress: Joi.when('hasDifferentFiscalAddress', {
        is: 'true',
        then: {
            address: Joi.string().required().messages({
                'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_ADDRESS,
            }),
            city: Joi.string()
                .required()
                .messages({ 'string.empty': texts.VEUILLEZ_SAISIR_LA_VILLE }),
            zipCode: Joi.string().required().messages({
                'string.empty': texts.VEUILLEZ_SAISIR_LE_CODE_POSTAL,
            }),
        },
        otherwise: Joi.optional(),
    }),
    wasUSFiscalResident: Joi.string().valid('true', 'false').required(),
    nifReason: Joi.when('nif', {
        is: '',
        then: Joi.string().required().messages({
            'string.empty': texts.VEUILLEZ_SAISIR_NIF_OR_MOTIF_ABSENCE_NIF,
        }),
        otherwise: Joi.any(),
    }),
    fiscalObligationOtherCountry: Joi.string().valid('true', 'false').required(),
    fiscalObligations: Joi.when('fiscalObligationOtherCountry', {
        is: 'true',
        then: Joi.array().items({
            country: Joi.string().required(),
            nifReason: Joi.when('nif', {
                is: '',
                then: Joi.string().required(),
                otherwise: Joi.any(),
            }),
        }),
        otherwise: Joi.any(),
    }),
    politicallyExposedPerson: Joi.string().valid('true', 'false').required(),
};

const CoSubscriberForm = ({
    hasStatusOk,
    isStepVerified,
    isPhoneVerified,
    handleNextStep,
    isOnClientSpace,
    isOnAdminSpace,
    specificSubscription,
    specificUser,
    refetch,
}) => {
    const dispatch = useDispatch();
    const internalUser = useSelector(userSelectors.getInternalUser);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    const tunnelErrors = useSelector(tunnelSelectors.getTunnelErrors);

    const [updateUser, { isSuccess, isLoading: updateLoader, error, isError, reset }] =
        useUpdateUserMutation();
    const [sendCoSubscriber, { isLoading: submitLoader, error: submitError }] =
        useSendCoSubscriberMutation();

    const subscription = useMemo(
        () =>
            isOnAdminSpace
                ? specificSubscription
                : isOnClientSpace
                ? currentSubscription
                : tunnelSubscription,
        [
            tunnelSubscription,
            specificSubscription,
            isOnClientSpace,
            isOnAdminSpace,
            currentSubscription,
        ]
    );
    const defaultForm = useMemo(
        () =>
            tunnelSchemas.initCoSubscriber(
                specificUser || internalUser,
                subscription?.coSubscriber,
                subscription
            ),
        [subscription, internalUser, specificUser]
    );
    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({});
    const [emailExists, setEmailExists] = useState(false);

    const handleGeneralInfoChange = useCallback((name, value) => {
        if (name === 'dateOfBirth') {
            setForm(prevState => ({
                ...prevState,
                generalInfo: {
                    ...prevState.generalInfo,
                    dateOfBirth: value,
                    birthDate: value ? value.format('YYYY-MM-DD') : '',
                },
            }));
        } else {
            setForm(prevState => ({
                ...prevState,
                generalInfo: {
                    ...prevState.generalInfo,
                    [name]: value,
                    nationality:
                        name === 'nativeCountry' || name === 'nationality'
                            ? value
                            : prevState.generalInfo.nationality,
                },
            }));
        }
    }, []);

    const handleAddressChange = useCallback(changedAddress => {
        setForm(prevState => ({
            ...prevState,
            address: { ...prevState.address, ...changedAddress },
        }));
    }, []);

    const handleSituationChange = useCallback((name, value) => {
        setForm(
            prevState => ({
                ...prevState,
                situation: { ...prevState.situation, [name]: value },
            }),
            []
        );
    }, []);

    const handleFiscalSituationChange = useCallback((name, value) => {
        setForm(prevState => {
            if (name === 'wasUSFiscalResident') {
                return {
                    ...prevState,
                    fiscalSituation: { ...prevState.fiscalSituation, [name]: value, tin: '' },
                };
            }
            return {
                ...prevState,
                fiscalSituation: { ...prevState.fiscalSituation, [name]: value },
            };
        });
        if (name === 'fiscalObligationOtherCountry') {
            setForm(prevState => ({
                ...prevState,
                fiscalObligations: [{ country: '', nif: '', nifReason: '' }],
            }));
        }
    }, []);

    const handleAddFiscalObligation = useCallback(() => {
        setForm(prevState => ({
            ...prevState,
            fiscalObligations: [
                ...prevState.fiscalObligations,
                { country: '', nif: '', nifReason: '' },
            ],
        }));
    }, []);

    const handleRemoveFiscalObligation = useCallback((e, index) => {
        setForm(prevState => {
            if (prevState.fiscalObligations.length === 1) {
                return {
                    ...prevState,
                    fiscalObligations: [{ country: '', nif: '', nifReason: '' }],
                };
            }
            return {
                ...prevState,
                fiscalObligations: [
                    ...prevState.fiscalObligations.slice(0, index),
                    ...prevState.fiscalObligations.slice(index + 1),
                ],
            };
        });
    }, []);

    const handleFiscalObligationChange = useCallback((e, index) => {
        const name = e.target.name;
        const value = name === 'nif' ? e.target.value.replace(/\s/gi, '') : e.target.value;
        setForm(prevState => ({
            ...prevState,
            fiscalObligations: [
                ...prevState.fiscalObligations.slice(0, index),
                {
                    ...prevState.fiscalObligations[index],
                    [name]: value,
                },
                ...prevState.fiscalObligations.slice(index + 1),
            ],
        }));
    }, []);

    const handleChangePoliticallyExposedReason = useCallback((e, key) => {
        const { checked } = e.target;
        setForm(prevState => ({
            ...prevState,
            politicallyExposed: {
                ...prevState.politicallyExposed,
                politicallyExposedReason: {
                    ...prevState.politicallyExposed.politicallyExposedReason,
                    [key]: checked,
                },
            },
        }));
    }, []);

    const handleChangePoliticallyExposedPerson = useCallback(e => {
        const { name, value } = e.target;
        setForm(prevState => ({
            ...prevState,
            politicallyExposed: {
                ...prevState.politicallyExposed,
                [name]: value,
                politicallyExposedReason: { 1: false, 2: false, 3: false },
            },
        }));
    }, []);

    const formIsValid = useCallback(() => {
        let { valid, newErrors } = validateSchema(schema, {
            ...form.generalInfo,
            ...form.situation,
            ...form.fiscalSituation,
            ...form.politicallyExposed,
            ...form.conditions,
            ...form.fiscalObligations,
            ...form.hasDifferentFiscalAddress,
            ...form.address,
        });
        if (form.politicallyExposed.politicallyExposedPerson === 'true') {
            const containsAtLeastOneTrue = Object.keys(
                form.politicallyExposed.politicallyExposedReason
            ).some(key => form.politicallyExposed.politicallyExposedReason[key] === true);
            valid = valid && containsAtLeastOneTrue;
        }
        if (
            (!form.generalInfo.birthDate || !dateValidator(form.generalInfo.birthDate)) &&
            !subscription?.indirectUntransferred
        ) {
            valid = false;
            newErrors.birthDate = texts.FORM_HAS_ERRORS;
        }
        setErrors(valid ? {} : newErrors);

        return valid;
    }, [form, subscription]);

    const changesMade = useMemo(
        () => !isEqual(defaultForm, form) || !hasStatusOk,
        [defaultForm, form, hasStatusOk]
    );

    const allowSubmit = useMemo(
        () =>
            form.generalInfo.birthDate &&
            form.generalInfo.birthPlace &&
            form.generalInfo.department &&
            form.generalInfo.nativeCountry &&
            form.generalInfo.nationality &&
            form.generalInfo.phoneNumber &&
            form.generalInfo.phoneNumber !== '+' &&
            form.address.address &&
            form.address.city &&
            form.address.zipCode &&
            form.address.country &&
            form.situation.profession &&
            form.fiscalSituation.taxResident &&
            form.fiscalSituation.isUSPerson &&
            form.situation.situation &&
            form.situation.noOfPersons !== '' &&
            form.situation.matrimonialRegime !== '' &&
            form.situation.activitySector !== '' &&
            form.fiscalSituation.wasUSFiscalResident !== null &&
            (form.fiscalSituation.nif !== '' || form.fiscalSituation.nifReason !== '') &&
            form.fiscalSituation.fiscalObligationOtherCountry !== null &&
            (form.fiscalSituation.fiscalObligationOtherCountry === 'false' ||
                form.fiscalObligations?.every(
                    fo => fo.country !== '' && (fo.nif !== '' || fo.nifReason !== '')
                )) &&
            form.politicallyExposed.politicallyExposedPerson !== null &&
            (form.politicallyExposed.politicallyExposedPerson === 'false' ||
                Object.values(form.politicallyExposed.politicallyExposedReason).some(
                    per => per === true
                )),
        [form]
    );

    const handleSubmit = useCallback(() => {
        reset();
        if (formIsValid() || isOnAdminSpace) {
            setEmailExists(false);
            if (isOnClientSpace || isOnAdminSpace) {
                updateUser({
                    id: subscription?.coSubscriber?.id,
                    profile: 'PP',
                    ...form,
                });
            } else {
                sendCoSubscriber({
                    payload: form,
                    resubmit: isStepVerified,
                    callback: () => handleNextStep(),
                });
            }
        }
    }, [
        formIsValid,
        form,
        updateUser,
        isStepVerified,
        sendCoSubscriber,
        handleNextStep,
        subscription,
        isOnClientSpace,
        isOnAdminSpace,
        reset,
    ]);

    useEffect(() => {
        dispatch(validationHandlerSet(formIsValid));
        return () => dispatch(validationHandlerSet());
    }, [dispatch, formIsValid]);

    useEffect(() => {
        if (isError && error?.data?.detail) {
            let errorKey = error.data.detail.split(':')[0];
            setErrors(prevState => ({ ...prevState, [errorKey]: 'error' }));
        }
    }, [isError, error]);

    useEffect(() => {
        if (isSuccess) {
            refetch?.();
        }
    }, [isSuccess, refetch]);

    // Handle mutation errors in side effect
    useEffect(() => {
        if (submitError) {
            if (submitError?.message === InternalErrors.USERNAME_EXISTS) {
                setEmailExists(true);
            }
            // Handle ineligible errors
            if (
                submitError?.data?.errors?.find(
                    err => err.message === investorErrors.CONTACT_INFO_US_PERSON
                )
            ) {
                dispatch(
                    ineligibleSet({
                        open: true,
                        message: texts.LA_SOUSCRIPTION_N_EST_PAS_POSSIBLE,
                    })
                );
            }
        }
    }, [submitError, dispatch]);

    const submitButtonText = useMemo(() => {
        if (isOnClientSpace || isOnAdminSpace) {
            return texts.SAVE;
        }
        return isStepVerified && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT;
    }, [isOnClientSpace, isOnAdminSpace, isStepVerified, changesMade]);

    return (
        <TunnelBox height="100%">
            <MemoizedGeneralInfoForm
                coSubscriber
                emailExists={emailExists}
                generalInfo={form.generalInfo}
                onChange={handleGeneralInfoChange}
                errors={errors}
                tunnelErrors={tunnelErrors}
                isPhoneVerified={isPhoneVerified}
                displayAllFields={isOnClientSpace || isOnAdminSpace}
                showIdentityInfo
            />
            <FullAddressForm
                address={form.address}
                label={texts.ADRESSE_DU_DOMICILE}
                onAddressChange={handleAddressChange}
                errors={errors}
                tunnelErrors={tunnelErrors}
            />
            <MemoizedSituationFormFPCIPP
                situation={form.situation}
                onChange={handleSituationChange}
                errors={errors}
                tunnelErrors={tunnelErrors}
            />
            <MemoizedFiscalSituationFormFCPRPP
                fiscalSituation={form.fiscalSituation}
                fiscalObligations={form.fiscalObligations}
                onChange={handleFiscalSituationChange}
                onAddLine={handleAddFiscalObligation}
                onRemoveLine={handleRemoveFiscalObligation}
                onUpdateLine={handleFiscalObligationChange}
                errors={errors}
                tunnelErrors={tunnelErrors}
            />
            <MemoizedPoliticallyExposedForm
                politicallyExposed={form.politicallyExposed}
                onChange={handleChangePoliticallyExposedPerson}
                onChangeReason={handleChangePoliticallyExposedReason}
            />
            {isSuccess && <SuccessMessage message={texts.YOUR_INFO_SAVED} />}
            <SubmitButton
                text={submitButtonText}
                onClick={changesMade || !isPhoneVerified ? handleSubmit : handleNextStep}
                disabled={
                    (!allowSubmit && !subscription.indirectUntransferred) ||
                    submitLoader ||
                    updateLoader
                }
                loading={submitLoader || updateLoader}
            />
        </TunnelBox>
    );
};

export default CoSubscriberForm;
